/**
 * useBookmarks Hook
 * Hook principal pour gérer les bookmarks avec cache et synchronisation
 */

"use client"

import { useCallback, useEffect, useState } from "react"
import { getUserBookmarks } from "../api/bookmarks-api"
import type {
    Bookmark,
    BookmarkError,
    UseBookmarksResult,
} from "../types/bookmark.types"
import {
    getCachedBookmarks,
    setCachedBookmarks,
} from "../utils/bookmark-storage"

interface UseBookmarksOptions {
  userEmail?: string
  enabled?: boolean
  refetchInterval?: number | false
}

export function useBookmarks(
  options: UseBookmarksOptions = {}
): UseBookmarksResult {
  const { userEmail, enabled = true, refetchInterval = false } = options

  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<BookmarkError | null>(null)

  const fetchBookmarks = useCallback(async () => {
    if (!enabled || !userEmail) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      // Essayer de charger depuis le cache d'abord
      const cached = getCachedBookmarks(userEmail)
      if (cached) {
        setBookmarks(cached)
        // Continue en arrière-plan pour rafraîchir
      }

      // Récupérer depuis le serveur
      const response = await getUserBookmarks(1, 100, { userEmail })
      setBookmarks(response.bookmarks)

      // Mettre à jour le cache
      setCachedBookmarks(userEmail, response.bookmarks)
    } catch (err: any) {
      const bookmarkError: BookmarkError = {
        code: err.code || "UNKNOWN_ERROR",
        message: err.message || "Failed to fetch bookmarks",
        details: err.details,
      }
      setError(bookmarkError)

      // En cas d'erreur, utiliser le cache si disponible
      const cached = getCachedBookmarks(userEmail)
      if (cached && bookmarks.length === 0) {
        setBookmarks(cached)
      }
    } finally {
      setIsLoading(false)
    }
  }, [enabled, userEmail, bookmarks.length])

  const refetch = useCallback(async () => {
    await fetchBookmarks()
  }, [fetchBookmarks])

  // Fetch initial
  useEffect(() => {
    fetchBookmarks()
  }, [fetchBookmarks])

  // Auto-refetch si configuré
  useEffect(() => {
    if (!refetchInterval || typeof refetchInterval !== "number") {
      return
    }

    const interval = setInterval(() => {
      fetchBookmarks()
    }, refetchInterval)

    return () => clearInterval(interval)
  }, [refetchInterval, fetchBookmarks])

  return {
    bookmarks,
    isLoading,
    error,
    refetch,
  }
}
